/**
 * 功能权限注册表实现
 * @module feature-permission-registry
 */

import { inject, injectable, multiInject, optional } from '@patsnap/tahiti-core/shared/inversify';
import {
    type FeaturePermissionRegistry,
    FeaturePermissionStrategy,
    type FeaturePermissionContext,
    type PermissionCheckResult,
} from '../feature-permission-types';
import type { FeatureKey } from '../types';
import { HeaderService } from '../../../services/header-service/header-service';

/**
 * 默认策略：直接放行
 */
class DefaultPermissionStrategy implements FeaturePermissionStrategy {
    readonly featureId = '' as FeatureKey;

    async checkPermission(_context: FeaturePermissionContext): Promise<PermissionCheckResult> {
        return {
            granted: true,
        };
    }
}

/**
 * 功能权限注册表
 * 通过 multiInject 自动收集所有注册的策略
 */
@injectable()
export class FeaturePermissionRegistryImpl implements FeaturePermissionRegistry {
    private _strategies = new Map<FeatureKey, FeaturePermissionStrategy>();
    private _defaultStrategy: FeaturePermissionStrategy = new DefaultPermissionStrategy();
    private _initialized = false;

    constructor(
        @multiInject(FeaturePermissionStrategy) @optional()
        private readonly _allStrategies: FeaturePermissionStrategy[] = [],
        @inject(HeaderService)
        private readonly _headerService: HeaderService,
    ) {
        // 构造函数中自动注册所有策略
        this._initStrategies();
    }

    /**
     * 初始化策略
     */
    private _initStrategies(): void {
        if (this._initialized) {
            return;
        }

        console.log(`[FeaturePermissionRegistry] Initializing with ${this._allStrategies.length} strategies`);

        this._allStrategies.forEach((strategy, index) => {
            if (!strategy.featureId) {
                console.warn(`[FeaturePermissionRegistry] Strategy #${index} has no featureId, skipping...`);
                return;
            }

            if (this._strategies.has(strategy.featureId)) {
                console.warn(`[FeaturePermissionRegistry] Feature "${strategy.featureId}" is already registered, overriding...`);
            }

            this._strategies.set(strategy.featureId, strategy);
            console.log(`[FeaturePermissionRegistry] Registered strategy for feature: ${strategy.featureId}`);
        });

        this._initialized = true;
        console.log(`[FeaturePermissionRegistry] Initialization complete. Total features: ${this._strategies.size}`);
    }

    /**
     * 设置默认策略
     */
    setDefaultStrategy(strategy: FeaturePermissionStrategy): void {
        this._defaultStrategy = strategy;
    }

    /**
     * 注册单个功能策略
     */
    register(featureId: FeatureKey, strategy: FeaturePermissionStrategy): void {
        if (this._strategies.has(featureId)) {
            console.warn(`[FeaturePermissionRegistry] Feature "${featureId}" is already registered, overriding...`);
        }
        this._strategies.set(featureId, strategy);
    }

    /**
     * 批量注册
     */
    registerMany(strategies: Record<FeatureKey, FeaturePermissionStrategy>): void {
        Object.entries(strategies).forEach(([featureId, strategy]) => {
            this.register(featureId as FeatureKey, strategy);
        });
    }

    /**
     * 获取策略
     */
    getStrategy(featureId: FeatureKey): FeaturePermissionStrategy | undefined {
        return this._strategies.get(featureId);
    }

    /**
     * 检查权限
     */
    async checkPermission(
        featureId: FeatureKey,
        context: FeaturePermissionContext,
    ): Promise<PermissionCheckResult> {
        const strategy = this.getStrategy(featureId) || this._defaultStrategy;

        if(this._headerService.account?.siteRegion === 'CN') {
            return {
                granted: true,
            };
        }

        try {
            const result = await strategy.checkPermission(context);
            return result;
        } catch (error) {
            console.error(`[FeaturePermissionRegistry] Error checking permission for "${featureId}":`, error);
            // 如果策略抛出异常，也返回拒绝结果
            return {
                granted: false,
                reason: 'strategy_error',
                message: error instanceof Error ? error.message : '权限检查失败',
            };
        }
    }

    /**
     * 记录使用量
     */
    async recordUsage(
        featureId: FeatureKey,
        context: FeaturePermissionContext,
    ): Promise<void> {
        const strategy = this.getStrategy(featureId);
        if (strategy?.recordUsage) {
            try {
                await strategy.recordUsage(context);
            } catch (error) {
                console.error(`[FeaturePermissionRegistry] Error recording usage for "${featureId}":`, error);
            }
        }
    }

    /**
     * 获取所有已注册的功能 ID
     */
    getRegisteredFeatures(): FeatureKey[] {
        return Array.from(this._strategies.keys());
    }

    /**
     * 清空所有注册的策略（主要用于测试）
     */
    clear(): void {
        this._strategies.clear();
    }
}
